import { motion } from "framer-motion";

/* ---------- Global micro particles (plus signs, hollow circles, dots) ---------- */
export function MicroParticles() {
  const items = [
    { t: "+", x: 6,  y: 12, s: 14 }, { t: "+", x: 88, y: 8,  s: 12 },
    { t: "+", x: 22, y: 78, s: 16 }, { t: "+", x: 72, y: 64, s: 12 },
    { t: "+", x: 48, y: 92, s: 10 }, { t: "o", x: 14, y: 40, s: 10 },
    { t: "o", x: 82, y: 30, s: 14 }, { t: "o", x: 38, y: 22, s: 8  },
    { t: "o", x: 60, y: 50, s: 12 }, { t: "o", x: 30, y: 60, s: 10 },
    { t: ".", x: 10, y: 70, s: 4  }, { t: ".", x: 50, y: 16, s: 4  },
    { t: ".", x: 78, y: 88, s: 4  }, { t: ".", x: 92, y: 54, s: 4  },
    { t: ".", x: 26, y: 36, s: 4  }, { t: ".", x: 66, y: 82, s: 4  },
  ];
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {items.map((p, i) => (
        <span key={i} className="absolute font-bold" style={{ left:`${p.x}%`, top:`${p.y}%`, fontSize:p.s, color:"#A78BFA", opacity:0.045, lineHeight:1 }}>
          {p.t === "o" ? "○" : p.t}
        </span>
      ))}
    </div>
  );
}

/* ---------- Welcome — meeting silhouettes (right panel, bottom) ---------- */
export function MeetingSilhouettes() {
  return (
    <svg viewBox="0 0 360 160" className="pointer-events-none absolute bottom-2 right-2 h-[150px] w-[320px]" style={{ opacity: 0.18 }} aria-hidden>
      <defs>
        <linearGradient id="ms-g" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#A78BFA" /><stop offset="100%" stopColor="#22D3EE" />
        </linearGradient>
      </defs>
      <line x1="20" y1="138" x2="340" y2="138" stroke="url(#ms-g)" strokeWidth="1" />
      {[{ x:60, h:70 }, { x:140, h:80 }, { x:220, h:68 }, { x:290, h:76 }].map((f, i) => (
        <g key={i} stroke="url(#ms-g)" strokeWidth="1.4" fill="none">
          <circle cx={f.x} cy={138 - f.h - 12} r="10" />
          <path d={`M${f.x-18},138 Q${f.x},${138-f.h} ${f.x+18},138`} />
        </g>
      ))}
    </svg>
  );
}

/* ---------- Welcome — floating shapes for the left panel ---------- */
export function FloatingShapes() {
  return (
    <svg viewBox="0 0 400 600" className="pointer-events-none absolute inset-0 h-full w-full" style={{ opacity: 0.18 }} aria-hidden>
      <defs>
        <linearGradient id="fs-g" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#A78BFA" stopOpacity="0.7" /><stop offset="100%" stopColor="#22D3EE" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      <circle cx="40" cy="80" r="18" fill="none" stroke="url(#fs-g)" strokeWidth="1" />
      <circle cx="340" cy="160" r="6" fill="url(#fs-g)" />
      <polygon points="60,300 76,310 76,330 60,340 44,330 44,310" fill="none" stroke="url(#fs-g)" strokeWidth="1" />
      <polygon points="320,420 336,430 336,450 320,460 304,450 304,430" fill="none" stroke="url(#fs-g)" strokeWidth="1" />
      <circle cx="120" cy="500" r="3" fill="#A78BFA" />
      <circle cx="280" cy="540" r="3" fill="#22D3EE" />
      <circle cx="200" cy="60"  r="2" fill="#e879f9" />
      <circle cx="60"  cy="420" r="2" fill="#A78BFA" />
    </svg>
  );
}

/* ---------- Outcome — ripple strip ---------- */
export function RippleStrip({ color }: { color: string }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color:"rgba(255,255,255,0.35)" }}>
        Your decision ripples through the team
      </div>
      <svg viewBox="0 0 320 80" className="h-[70px] w-full" aria-hidden>
        {[20,35,50,65,80].map((r,i) => (
          <motion.ellipse key={r} cx="160" cy="40" rx={r*1.6} ry={r*0.6} fill="none" stroke={color} strokeWidth="1"
            initial={{ opacity:0 }} animate={{ opacity:[0,0.18,0] }}
            transition={{ duration:3.6, repeat:Infinity, delay:i*0.4, ease:"easeOut" }} />
        ))}
        <circle cx="160" cy="40" r="3" fill={color} opacity="0.6" />
      </svg>
    </div>
  );
}

/* ---------- Open text — ink splash ---------- */
export function InkSplash() {
  return (
    <svg viewBox="0 0 300 120" className="pointer-events-none mx-auto h-[110px] w-full max-w-[360px]" style={{ opacity:0.2 }} aria-hidden>
      <defs>
        <radialGradient id="ink-g" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#A78BFA" stopOpacity="0.8" /><stop offset="100%" stopColor="#A78BFA" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="150" cy="60" rx="90" ry="32" fill="url(#ink-g)" />
      <circle cx="60" cy="40" r="4" fill="#A78BFA" opacity="0.5" />
      <circle cx="240" cy="80" r="3" fill="#22D3EE" opacity="0.5" />
      <circle cx="200" cy="30" r="2" fill="#e879f9" opacity="0.5" />
      <path d="M70 90 Q150 30 230 90" fill="none" stroke="#A78BFA" strokeWidth="1" strokeDasharray="3 5" opacity="0.6" />
    </svg>
  );
}

/* ---------- Reflection — vertical line-art figure looking at reflection ---------- */
export function ReflectionFigure() {
  return (
    <svg viewBox="0 0 120 160" className="h-[140px] w-full" style={{ opacity:0.7 }} aria-hidden>
      <defs>
        <linearGradient id="rf-g" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#A78BFA" /><stop offset="100%" stopColor="#22D3EE" />
        </linearGradient>
      </defs>
      <line x1="60" y1="0" x2="60" y2="160" stroke="url(#rf-g)" strokeWidth="0.5" strokeDasharray="2 4" opacity="0.5" />
      <g stroke="url(#rf-g)" strokeWidth="1.2" fill="none">
        <circle cx="40" cy="50" r="10" />
        <path d="M40,60 L40,100" /><path d="M40,75 L25,90" /><path d="M40,75 L55,85" />
        <path d="M40,100 L30,140" /><path d="M40,100 L50,140" />
      </g>
      <g stroke="url(#rf-g)" strokeWidth="1" fill="none" opacity="0.45">
        <circle cx="80" cy="50" r="10" />
        <path d="M80,60 L80,100" /><path d="M80,75 L95,90" /><path d="M80,75 L65,85" />
        <path d="M80,100 L90,140" /><path d="M80,100 L70,140" />
      </g>
    </svg>
  );
}

/* ---------- Complete — hex achievement badge ---------- */
export function HexBadge({ label, icon, score, index }: { label:string; icon:string; score:number; index:number }) {
  const earned = score >= 70;
  return (
    <motion.div initial={{ scale:0, opacity:0, y:10 }} animate={{ scale:1, opacity:1, y:0 }}
      transition={{ type:"spring", stiffness:260, damping:16, delay:0.2+index*0.08 }}
      className="flex flex-col items-center gap-1.5">
      <div className="relative flex h-[68px] w-[60px] items-center justify-center"
        style={{
          clipPath:"polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)",
          background: earned ? "linear-gradient(135deg, #8B5CF6, #22D3EE)" : "rgba(255,255,255,0.05)",
          boxShadow: earned ? "0 0 18px rgba(139,92,246,0.45)" : "none",
        }}>
        <span className="text-[22px]" style={{ filter:earned?"none":"grayscale(1)", opacity:earned?1:0.35 }}>
          {earned ? icon : "🔒"}
        </span>
      </div>
      <div className="max-w-[80px] text-center text-[9px] font-medium uppercase tracking-wide"
        style={{ color: earned?"rgba(255,255,255,0.75)":"rgba(255,255,255,0.3)" }}>
        {label}
      </div>
    </motion.div>
  );
}
